import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

import API from '@/config/api';
// API imported from config;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/payments/status/${sessionId}`,
        getAuthHeaders()
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(response.data);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      setStatus('error');
    }
  };

  const getContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center">
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-primary animate-spin" />
            <h2 className="font-heading font-bold text-2xl mb-2">Processing Payment</h2>
            <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
          </div>
        );
      case 'success':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="font-heading font-bold text-2xl mb-2">Payment Successful!</h2>
            <p className="text-muted-foreground mb-6">
              Thank you for your payment. Your invoice has been marked as paid.
            </p>
            {paymentData && (
              <div className="bg-muted rounded-xl p-4 mb-6 text-left max-w-sm mx-auto">
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-semibold">${(paymentData.amount_total / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <span className="text-green-600 font-semibold">Paid</span>
                </div>
              </div>
            )}
            <Button onClick={() => navigate('/billing')} data-testid="back-to-billing-btn">
              Back to Billing
            </Button>
          </div>
        );
      case 'expired':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-12 h-12 text-amber-600" />
            </div>
            <h2 className="font-heading font-bold text-2xl mb-2">Payment Session Expired</h2>
            <p className="text-muted-foreground mb-6">
              Your payment session has expired. Please try again.
            </p>
            <Button onClick={() => navigate('/billing')} data-testid="try-again-btn">
              Try Again
            </Button>
          </div>
        );
      case 'timeout':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-12 h-12 text-amber-600" />
            </div>
            <h2 className="font-heading font-bold text-2xl mb-2">Payment Processing</h2>
            <p className="text-muted-foreground mb-6">
              Your payment is being processed. Please check your email for confirmation.
            </p>
            <Button onClick={() => navigate('/billing')} data-testid="check-status-btn">
              Check Status
            </Button>
          </div>
        );
      default:
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="font-heading font-bold text-2xl mb-2">Payment Error</h2>
            <p className="text-muted-foreground mb-6">
              Something went wrong with your payment. Please try again or contact support.
            </p>
            <Button onClick={() => navigate('/billing')} data-testid="go-back-btn">
              Go Back
            </Button>
          </div>
        );
    }
  };

  return (
    <DashboardLayout title="Payment Status">
      <div className="max-w-lg mx-auto" data-testid="payment-success-page">
        <Card className="card-clay">
          <CardContent className="p-8">
            {getContent()}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PaymentSuccessPage;
